import type { ErrorEvent } from '@sentry/nextjs';

/**
 * 스택 트레이스를 분석하여 브라우저 플러그인/확장 프로그램에서 발생한 에러인지 판별합니다.
 *
 * 브라우저 확장 프로그램이 페이지에 스크립트를 주입하여 발생하는 에러들은
 * 우리 코드의 문제가 아니므로 필터링합니다.
 *
 * @param event - Sentry ErrorEvent 객체
 * @returns 브라우저 플러그인 에러인 경우 true
 *
 * @example
 * ```typescript
 * import { isBrowserPluginErrorByStack } from '@zep-us/sentry';
 *
 * Sentry.init({
 *   beforeSend(event, hint) {
 *     if (isBrowserPluginErrorByStack(event)) {
 *       return null; // 플러그인 에러는 무시
 *     }
 *     return event;
 *   },
 * });
 * ```
 *
 * 감지하는 플러그인 유형:
 * - 브라우저 자동완성: `execute_auto_fill` (함수 이름 기반)
 * - Chrome 확장 프로그램: `chrome-extension://`
 * - Firefox 확장 프로그램: `moz-extension://`
 * - Safari 확장 프로그램: `safari-extension://`
 * - Edge 확장 프로그램: `edge-extension://`
 * - 중국 브라우저 주입 스크립트 (예방적 필터링):
 *   - QQ Browser: `qqbrowser`, `qq.com`
 *   - UC Browser: `ucbrowser`, `uc.cn`
 *   - Sogou Browser: `sogou`, `se.360.cn`
 *   - 360 Browser: `so.com`, `360safe`
 *   - Baidu Browser: `baidu`
 *   - Maxthon Browser: `maxthon`
 * - 사용자 스크립트: `userscript`, `tampermonkey`, `greasemonkey`
 * - 기타: `injected-scripts/`
 *
 * 실제 발생한 이슈:
 * - https://zep-us.sentry.io/issues/6236950788 (브라우저 플러그인)
 * - https://zep-us.sentry.io/issues/6838563405 (injected-scripts)
 *
 * Note: 중국 브라우저 패턴은 알려진 스크립트 주입 동작을 기반으로 예방적으로 추가됨
 */
export function isBrowserPluginErrorByStack(event: ErrorEvent): boolean {
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      // 스택 트레이스에서 플러그인 관련 프레임 체크
      if (exception.stacktrace?.frames) {
        const hasPluginFrame = exception.stacktrace.frames.some(frame => {
          const filename = frame.filename || '';
          const lowerFilename = filename.toLowerCase();
          const functionName = frame.function || '';
          const lowerFunctionName = functionName.toLowerCase();

          // 브라우저 자동완성 확장 프로그램 (QUIZ-APP-64)
          // - Android Chrome, 삼성 인터넷 등에서 자동완성 기능이 주입하는 스크립트
          // - <anonymous> 스택에서 execute_auto_fill 함수 호출
          // Ref: https://zep-us.sentry.io/issues/6236330938/
          if (lowerFunctionName.includes('execute_auto_fill')) {
            return true;
          }

          // 브라우저 확장 프로그램
          if (
            filename.includes('chrome-extension://') ||
            filename.includes('moz-extension://') ||
            filename.includes('safari-extension://') ||
            filename.includes('edge-extension://')
          ) {
            return true;
          }

          // 중국 브라우저 주입 스크립트
          if (
            lowerFilename.includes('qqbrowser') ||
            lowerFilename.includes('qq.com') ||
            lowerFilename.includes('ucbrowser') ||
            lowerFilename.includes('uc.cn') ||
            lowerFilename.includes('sogou') ||
            lowerFilename.includes('se.360.cn') ||
            lowerFilename.includes('so.com') ||
            lowerFilename.includes('360safe') ||
            lowerFilename.includes('baidu') ||
            lowerFilename.includes('maxthon')
          ) {
            return true;
          }

          // 사용자 스크립트 및 기타 주입 스크립트
          if (
            lowerFilename.includes('injected-scripts/') ||
            lowerFilename.includes('userscript') ||
            lowerFilename.includes('tampermonkey') ||
            lowerFilename.includes('greasemonkey')
          ) {
            return true;
          }

          return false;
        });

        if (hasPluginFrame) {
          return true;
        }
      }
    }
  }

  return false;
}
